import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import router from '@/router'
import { cachedGet, invalidateByPrefix } from './cache'

/** 后端统一响应包装 com.royaltiger.hrms.common.web.R */
interface ApiResponse<T = unknown> {
  code: number
  msg: string
  data: T
  requestId?: string
}

/** 业务扩展配置: silent (UAT-FB#29 跳过拦截器 toast) / retry (UAT-FB#49 非幂等请求显式开启重试) */
export interface RequestConfig extends AxiosRequestConfig {
  silent?: boolean
  retry?: boolean
  /** 候选人/外部人员持 token 访问的公开接口，不要求系统登录态。 */
  anonymous?: boolean
}

/**
 * 响应拦截器把 R<T> 包装拆掉后, promise resolve 的是 data 本身而非 AxiosResponse,
 * 所以对外导出的实例类型上, 各方法返回 Promise<T> (默认 any).
 */
interface UnwrappedClient {
  <T = any, R = T>(config: RequestConfig): Promise<R>
  <T = any, R = T>(url: string, config?: RequestConfig): Promise<R>
  request<T = any, R = T>(config: RequestConfig): Promise<R>
  get<T = any, R = T>(url: string, config?: RequestConfig): Promise<R>
  delete<T = any, R = T>(url: string, config?: RequestConfig): Promise<R>
  head<T = any, R = T>(url: string, config?: RequestConfig): Promise<R>
  options<T = any, R = T>(url: string, config?: RequestConfig): Promise<R>
  post<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  put<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  patch<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  postForm<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  putForm<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  patchForm<T = any, R = T>(url: string, data?: any, config?: RequestConfig): Promise<R>
  defaults: AxiosInstance['defaults']
  interceptors: AxiosInstance['interceptors']
  getUri(config?: RequestConfig): string
}

const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
})

// ─── 请求拦截器：自动注入 Bearer token + 未登录拦闸 ─────────────────
//   未登录时, 大量 store 在模块加载即 fetch → 一批带鉴权请求全 403 → 刷屏 & 浪费。
//   这里直接取消 (未登录 + 非 /auth/ 接口), 从源头掐掉。这些请求本就没 token 必 403、
//   拿不到数据, 取消零功能损失。/auth/* 与显式 anonymous 的 token 接口放行。
request.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const userStore = useUserStore()
  if (userStore.token) {
    cfg.headers.Authorization = `Bearer ${userStore.token}`
    return cfg
  }
  const url = cfg.url || ''
  if (/\/auth\//.test(url) || (cfg as RequestConfig).anonymous === true) return cfg
  return Promise.reject(new axios.Cancel(`未登录, 跳过认证请求: ${url}`))
})

// 系统级 toast 节流: 同一条消息 2s 内只弹一次, 防批量失败 (会话过期等) 刷屏
const __lastToastAt: Record<string, number> = {}
function toastOnce(kind: 'warning' | 'error', msg: string): void {
  const now = Date.now()
  if (now - (__lastToastAt[msg] || 0) < 2000) return
  __lastToastAt[msg] = now
  ElMessage[kind](msg)
}

function formatBusinessError(config: AxiosRequestConfig, response: ApiResponse): string {
  const method = (config.method || 'get').toUpperCase()
  const endpoint = config.url || '未知接口'
  const message = `请求失败（${method} ${endpoint}，错误码 ${response.code}）：${response.msg || '未知业务错误'}`
  return withRequestId(message, response.requestId)
}

function withRequestId(message: string, requestId?: string): string {
  if (!requestId || message.includes(requestId)) return message
  return `${message}（request-id: ${requestId}）`
}

// ─── 响应拦截器：拆 R 包装 + 401 自动登出 ──────────────────────────
//
// UAT-FB#29 (2026-06-15): config 加可选 silent 字段 — 调用方 try-catch 自己处理时,
//   不让拦截器弹 toast (避免顶部红色 "Network Error" 跟下方 fallback 提示同时出现).
//   用法: request.get('/xxx', { silent: true })
//        或 procApi.instance.getByBizNo(no, { silent: true })
request.interceptors.response.use(
  (resp: AxiosResponse): any => {
    const r = resp.data as ApiResponse | unknown
    const silent = (resp.config as any)?.silent === true
    // 后端 R<T> 包装：{ code:0, msg:"OK", data:T }；非 0 视为业务异常
    if (r && typeof r === 'object' && 'code' in r && typeof (r as ApiResponse).code === 'number') {
      const wrapped = r as ApiResponse
      if (wrapped.code === 0) return wrapped.data
      // Controller/全局异常处理器可能以 HTTP 200 + R.code 返回业务错误，鉴权判断必须看 body code。
      const isSessionExpired = wrapped.code === 401
        && /未登录|登录.*过期|登录态.*过期/.test(wrapped.msg || '')
      if (isSessionExpired && useUserStore().token) {
        const userStore = useUserStore()
        toastOnce('warning', '登录已过期，请重新登录')
        userStore.clearAuth()
        if (router.currentRoute.value.path !== '/login') router.replace('/login')
        return Promise.reject(wrapped)
      }
      // V89: code=409 (DUPLICATE) 不弹 toast, 由上层 catch 弹 confirm
      // UAT-FB#29: silent=true 也跳过 toast
      if (wrapped.code !== 409 && !silent) {
        // 权限拒绝直接展示后端给出的可操作原因；其它业务错误保留接口信息，便于定位。
        const message = wrapped.code === 403
          ? withRequestId(wrapped.msg || '无权访问', wrapped.requestId)
          : formatBusinessError(resp.config, wrapped)
        toastOnce('error', message)
      }
      return Promise.reject(wrapped)
    }
    return r
  },
  async (err: AxiosError<ApiResponse>) => {
    // 未登录拦闸取消的请求: 静默透传, 不弹任何 toast
    if (axios.isCancel(err)) return Promise.reject(err)

    const status = err.response?.status
    const userStore = useUserStore()
    const silent = (err.config as any)?.silent === true

    // UAT-FB#49 (2026-06-15): InPrivate 自签证书 / 临时网络抖动 自动 retry 1 次
    //   只对 Network Error (无 response.status) 或 5xx 重试, 4xx 视为业务/权限拒绝不重试.
    //   GET/HEAD 幂等可直接重试; POST/PUT/DELETE 默认不重试 (避免副作用), 调用方可显式 config.retry=true.
    const cfg: any = err.config || {}
    const isNetworkErr = !status || status >= 500
    const method = (cfg.method || 'get').toLowerCase()
    const idempotent = method === 'get' || method === 'head' || cfg.retry === true
    if (isNetworkErr && idempotent && !cfg.__retried) {
      cfg.__retried = true
      // 短延迟让浏览器接受自签证书
      await new Promise(resolve => setTimeout(resolve, 600))
      try {
        return await request(cfg)
      } catch (retryErr) {
        // YL0013 (2026-07-09): retry 的 rejection 已在它自己的拦截器 pass 里完成 toast/401 处理
        // (cfg.__retried=true 不会再进重试分支). 必须直接透传, 不能落到下方报错路径 —
        // 否则同一次失败会连弹 2 个相同 toast (retry pass 一次 + 原始 err fallthrough 一次).
        return Promise.reject(retryErr)
      }
    }

    const requestId = err.response?.data?.requestId
      || (err.response?.headers?.['x-request-id'] as string | undefined)
    const backendMessage = err.response?.data?.msg
    const isTimeout = err.code === 'ECONNABORTED'
      || err.code === 'ETIMEDOUT'
      || /timeout/i.test(err.message || '')
    let displayMessage = backendMessage || err.message || '请求失败，请稍后重试'
    if (!status) {
      displayMessage = isTimeout ? '请求超时，请稍后重试' : '网络异常，请稍后重试'
    } else if (status >= 500 && !backendMessage) {
      displayMessage = '服务器暂时不可用，请稍后重试'
    } else if (status === 401) {
      displayMessage = backendMessage || '登录已过期，请重新登录'
    } else if (status === 403) {
      displayMessage = backendMessage || '无权访问'
    }
    // 页面层经常继续读取 e.message；同步写回中文，避免局部 catch 再弹出 Network Error。
    displayMessage = withRequestId(displayMessage, requestId)
    err.message = displayMessage

    if (status === 401) {
      // 只有 401 才表示无效/过期会话；403 是有效账号的权限拒绝，不能清空登录态。
      toastOnce('warning', '登录已过期，请重新登录')
      // 直接清本地（不调 store.logout，避免重新触发 /auth/logout 进入回环）
      userStore.clearAuth()
      if (router.currentRoute.value.path !== '/login') {
        router.replace('/login')
      }
    } else if (status === 403) {
      // 登录接口的 403 可能是 CORS / Origin 安全策略在账号校验前拒绝，
      // 交由 Login.vue 给出针对性提示，不能误报为账号“无权访问”。
      const isLoginRequest = /\/auth\/login(?:$|\?)/.test(err.config?.url || '')
      if (!silent && !isLoginRequest) toastOnce('error', '无权访问')
    } else if (!silent) {
      toastOnce('error', displayMessage)
    }
    return Promise.reject(err)
  },
)

// ─── 请求级缓存层接线 (api/cache.ts) ──────────────────────────────
//   GET: 并发去重默认全开 (零陈旧风险); TTL 缓存仅调用方传 { cache: ms } 才开启.
//   写操作: 按 url 前缀失效对应 TTL 缓存, 防脏读.
//   收口在此处, 全站 request.get 调用方零改动自动生效.
const rawGet = request.get.bind(request)
request.get = ((url: string, config?: RequestConfig) =>
  cachedGet(rawGet, url, config)) as typeof request.get

;(['post', 'put', 'patch', 'delete'] as const).forEach((method) => {
  const raw = (request[method] as any).bind(request)
  request[method] = ((url: string, ...rest: any[]) => {
    // 去掉 query, 按资源路径失效 (GET 缓存 key 以同 url 前缀存)
    invalidateByPrefix(String(url).split('?')[0])
    return raw(url, ...rest)
  }) as any
})

export default request as UnwrappedClient
